import { useRef } from "react";
import { ArrowTopRightOnSquareIcon, ArrowUpTrayIcon, CheckCircleIcon, DocumentTextIcon, PaperAirplaneIcon, PencilSquareIcon, TrashIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { Button, Card, Input, Modal, RadioGroup, RowAction, SearchableSelect, SectionTitle, Select } from "../../ui";
import { ABROAD_COUNTRY_OPTIONS, countryName, formatKigaliDate } from "../model";
import { TYPE_NAMES, fileSummary, formatSize, sectorLabel } from "../review/model";

// The parts of a student's application form that scholarships and abroad loans share; `field(name)` gives an input its props

export const YES_NO = [
  { value: "no", label: "No" },
  { value: "yes", label: "Yes" },
];

// Where the student was admitted: anywhere abroad with the country (`abroad`), else an institution in Rwanda
export function AdmissionFields({ field, abroad }) {
  return abroad ? (
    <>
      <SearchableSelect label="Country where you will study" required placeholder="Type or select a country" options={ABROAD_COUNTRY_OPTIONS} {...field("studyCountryCode")} />
      <Input label="University that admitted you" required maxLength={200} placeholder="e.g. Tsinghua University" {...field("admittedInstitution")} />
      <Input label="Course you were admitted to" required maxLength={200} placeholder="e.g. MSc Civil Engineering" {...field("admittedProgramme")} />
    </>
  ) : (
    <>
      <Input label="Institution that admitted you" required maxLength={200} placeholder="e.g. Kigali Independent University" {...field("admittedInstitution")} />
      <Input label="Programme you were admitted to" required maxLength={200} {...field("admittedProgramme")} />
    </>
  );
}

export function EmploymentFields({ form, field }) {
  return (
    <>
      <RadioGroup label="Are you currently employed?" name="employed" required options={YES_NO} {...field("employed")} />
      {form.employed === "yes" && <Input label="Employer" required maxLength={200} {...field("employer")} />}
    </>
  );
}

// The funding a loan asks for; `started` holds the props of the "already started" question
export function FundingFields({ form, field, started, currencyOptions, amounts }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Select label="Currency" required placeholder="Choose a currency" options={currencyOptions} {...field("currency")} />
      {amounts.map(({ key, label, required }) => (
        <Input key={key} type="number" min={0} step={1} label={label} required={required} suffix={form.currency || undefined} {...field(key)} />
      ))}
      <Input type="date" label="Programme start date" required {...field("studyStartDate")} />
      <Input type="date" label="Programme end date" required min={form.studyStartDate || undefined} {...field("studyEndDate")} />
      <RadioGroup className="md:col-span-2" label="Have you already started and need support for the remaining period?" name="started" options={YES_NO} {...started} />
      {form.started === "yes" && (
        <>
          <Input type="number" min={0} step={1} label="Months already covered" {...field("coveredPeriodMonths")} />
          <Input type="number" min={0} step={1} label="Months remaining" {...field("remainingPeriodMonths")} />
        </>
      )}
    </div>
  );
}

// One required document: what is uploaded, and a button to upload or replace it
export function RequirementRow({ requirement, document, editable, busy, error, onUpload, onDelete, onOpen }) {
  const input = useRef(null);
  const accept = requirement.allowedTypes.join(",");
  return (
    <li className={`flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between ${error ? "rounded-md bg-red-50 px-3" : ""}`}>
      <div className="flex min-w-0 items-start gap-3">
        {document ? <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-600" /> : <DocumentTextIcon className="h-5 w-5 shrink-0 text-slate-400" />}
        <div className="min-w-0">
          <p className="font-medium text-slate-800">
            {requirement.label} {!requirement.mandatory && <span className="text-sm font-normal text-slate-500">(optional)</span>}
          </p>
          {requirement.description && <p className="text-sm text-slate-500">{requirement.description}</p>}
          <p className="text-xs text-slate-500">
            {requirement.allowedTypes.map((t) => TYPE_NAMES[t] ?? t).join(", ")} · up to {formatSize(requirement.maxBytes ?? 10 * 1024 * 1024)}
          </p>
          {document && <p className="truncate text-sm text-slate-700">Uploaded · {fileSummary(document)}</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {document && <RowAction Icon={ArrowTopRightOnSquareIcon} label="Open" variant="ghost" onClick={() => onOpen(document)} />}
        {editable && document && <RowAction Icon={TrashIcon} label="Remove" variant="ghost" onClick={() => onDelete(document)} disabled={busy} />}
        {editable && (
          <>
            <Button size="sm" variant="outline" icon={<ArrowUpTrayIcon className="h-4 w-4" />} onClick={() => input.current?.click()} loading={busy}>
              {document ? "Replace" : "Upload"}
            </Button>
            <input
              ref={input}
              type="file"
              accept={accept}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) onUpload(requirement, file);
              }}
            />
          </>
        )}
      </div>
    </li>
  );
}

// The consent statement, the student's agreement and the submit button; `messages` are problems about the whole application
export function DeclarationCard({ title, consent, agreed, onAgree, error, messages, onSubmit, submitting, submitLabel = "Submit application", submitIcon = <PaperAirplaneIcon className="h-4 w-4" /> }) {
  return (
    <Card title={title}>
      <p className="rounded-md bg-slate-50 p-4 text-sm text-slate-700">{consent.text}</p>
      <label className="mt-4 flex items-start gap-3 text-sm text-slate-800">
        <input type="checkbox" className="mt-0.5 h-4 w-4 accent-brand" checked={agreed} onChange={(e) => onAgree(e.target.checked)} />I have read the declaration and I agree.
      </label>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {messages.map((message) => (
        <p key={message} className="mt-2 flex items-center gap-2 text-sm text-red-600">
          <XCircleIcon className="h-4 w-4" /> {message}
        </p>
      ))}
      <div className="mt-4 flex justify-end">
        <Button onClick={onSubmit} loading={submitting} disabled={!agreed} icon={submitIcon}>
          {submitLabel}
        </Button>
      </div>
    </Card>
  );
}

const YES_NO_LABELS = { yes: "Yes", no: "No", "": "—" };

// The form's answers as the preview lists them: admission and loan parts only where the form asks them
export const answerRowsOf = (form, { admission, loan, amounts = [] }) => [
  ...(admission
    ? [
        ["Admitted to", form.admittedInstitution],
        ["Programme admitted to", form.admittedProgramme],
        ["Country of study", form.studyCountryCode && countryName(form.studyCountryCode)],
      ]
    : []),
  ...(loan ? [["Priority sector", form.courseSector && sectorLabel(form.courseSector)]] : []),
  ["Employed", YES_NO_LABELS[form.employed]],
  ...(form.employed === "yes" ? [["Employer", form.employer]] : []),
  ...(loan ? amounts.filter(({ key }) => form[key]).map(({ key, label }) => [label, `${form[key]} ${form.currency}`]) : []),
];

// What the student is about to send, as HEC will read it: Submit waits for every required document
export function PreviewModal({ subtitle, studies, answers, requirements, hasDocument, closesAt, submitting, onBack, onSubmit }) {
  const missing = requirements.filter((r) => r.mandatory && !hasDocument(r));
  return (
    <Modal
      open
      onClose={onBack}
      dismissible={!submitting}
      size="xl"
      title="Check your application before sending it"
      description={subtitle}
      footer={
        <>
          <Button variant="ghost" onClick={onBack} disabled={submitting} icon={<PencilSquareIcon className="h-4 w-4" />}>
            Back to edit
          </Button>
          <Button onClick={onSubmit} loading={submitting} disabled={missing.length > 0} icon={<PaperAirplaneIcon className="h-4 w-4" />}>
            Submit application
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-3">
          <SectionTitle title="Your studies" />
          {studies}
        </section>
        <section className="flex flex-col gap-3">
          <SectionTitle title="About this application" />
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {answers.map(([label, value]) => (
              <div key={label}>
                <dt className="text-slate-500">{label}</dt>
                <dd className="font-medium text-slate-800">{value || "—"}</dd>
              </div>
            ))}
          </dl>
        </section>
        <section className="flex flex-col gap-3">
          <SectionTitle title="Documents" />
          <ul className="flex flex-col gap-1 text-sm">
            {requirements.map((r) => (
              <li key={r.id} className="flex flex-wrap gap-x-2">
                <span className="font-medium text-slate-800">{r.label}</span>
                <span className={hasDocument(r) ? "text-slate-600" : r.mandatory ? "text-red-600" : "text-slate-400"}>
                  {hasDocument(r) ? "uploaded" : r.mandatory ? "missing" : "not added (optional)"}
                </span>
              </li>
            ))}
          </ul>
          {missing.length > 0 && <p className="text-sm text-red-600">Add the missing documents before submitting.</p>}
        </section>
        <p className="text-sm text-slate-600">Until the deadline ({formatKigaliDate(closesAt)}) you can still take it back to change it, then submit it again.</p>
      </div>
    </Modal>
  );
}
